import icoBath from "../../assets/icons/ico_bath.svg"
import icoBretzel from "../../assets/icons/ico_bretzel.svg"
import icoCharging from "../../assets/icons/ico_charging.svg"
import icoDryer from "../../assets/icons/ico_dryer.svg"
import icoFood from "../../assets/icons/ico_food.svg"
import icoGym from "../../assets/icons/ico_gym.svg"
import icoHeating from "../../assets/icons/ico_heating.svg"
import icoMessage from "../../assets/icons/ico_message.svg"
import icoMusic from "../../assets/icons/ico_music.svg"
import icoPhone from "../../assets/icons/ico_phone.svg"
import icoStreaming from "../../assets/icons/ico_streaming.svg"
import icoTVTower from "../../assets/icons/ico_tv_tower.svg"
import icoZoo from "../../assets/icons/ico_zoo.svg"
import icoEqualCO2 from "../../assets/icons/ico_equal_co2.svg"
import icoEqualCost from "../../assets/icons/ico_equal_cost.svg"

export enum EquivalenceMainType {
  co2,
  cost
}

export enum EquivalenceTypes {
  message,
  streaming,
  washing,
  showering,
  phone,
  heating,
  electricity,
  bretzel,
  tvTower,
  musicStreaming,
  wilhelma,
  food,
  fitness
}

export class Equivalence {
  static getEquivalenceTypesForMainType(mainType: EquivalenceMainType): EquivalenceTypes[] {
    if (mainType === EquivalenceMainType.co2) {
      return [
        EquivalenceTypes.message,
        EquivalenceTypes.streaming,
        EquivalenceTypes.showering,
        EquivalenceTypes.washing,
        EquivalenceTypes.phone,
        EquivalenceTypes.heating,
        EquivalenceTypes.electricity
      ]
    } else {
      return [
        EquivalenceTypes.bretzel,
        EquivalenceTypes.tvTower,
        EquivalenceTypes.musicStreaming,
        EquivalenceTypes.wilhelma,
        EquivalenceTypes.food,
        EquivalenceTypes.fitness
      ]
    }
  }

  private static getMainTypeFromType(type: EquivalenceTypes): EquivalenceMainType {
    if (
      type === EquivalenceTypes.message ||
      type === EquivalenceTypes.streaming ||
      type === EquivalenceTypes.showering ||
      type === EquivalenceTypes.washing ||
      type === EquivalenceTypes.phone ||
      type === EquivalenceTypes.heating ||
      type === EquivalenceTypes.electricity
    ) {
      return EquivalenceMainType.co2
    } else {
      return EquivalenceMainType.cost
    }
  }

  static equivalenceValueMap = new Map<EquivalenceTypes, number>([
    [EquivalenceTypes.message, 0.014],
    [EquivalenceTypes.streaming, 4],
    [EquivalenceTypes.washing, 2300],
    [EquivalenceTypes.showering, 1000],
    [EquivalenceTypes.phone, 63000],
    [EquivalenceTypes.heating, 460000],
    [EquivalenceTypes.electricity, 700000],
    [EquivalenceTypes.bretzel, 0.9],
    [EquivalenceTypes.tvTower, 10.5],
    [EquivalenceTypes.musicStreaming, 10],
    [EquivalenceTypes.wilhelma, 20],
    [EquivalenceTypes.food, 35],
    [EquivalenceTypes.fitness, 25]
  ])

  private static equivalenceIconMap = new Map<EquivalenceTypes, string>([
    [EquivalenceTypes.message, icoMessage],
    [EquivalenceTypes.streaming, icoStreaming],
    [EquivalenceTypes.washing, icoDryer],
    [EquivalenceTypes.showering, icoBath],
    [EquivalenceTypes.phone, icoPhone],
    [EquivalenceTypes.heating, icoHeating],
    [EquivalenceTypes.electricity, icoCharging],
    [EquivalenceTypes.bretzel, icoBretzel],
    [EquivalenceTypes.tvTower, icoTVTower],
    [EquivalenceTypes.musicStreaming, icoMusic],
    [EquivalenceTypes.wilhelma, icoZoo],
    [EquivalenceTypes.food, icoFood],
    [EquivalenceTypes.fitness, icoGym]
  ])

  static getCostString(type: EquivalenceTypes): string {
    var cost = this.equivalenceValueMap.get(type)

    if (cost) {
      if (cost % 1 === 0) {
        return cost + " €"
      } else {
        return cost.toFixed(2).toString().replaceAll(".", ",") + " €"
      }
    }

    return ""
  }

  static getMessage(type: EquivalenceTypes, value: number): string {
    switch (type) {
      case EquivalenceTypes.bretzel:
        if (value === 1) {
          return "Brezel"
        } else {
          return "Brezeln"
        }
      case EquivalenceTypes.electricity:
        if (value === 1) {
          return "Jahr Strom verbrauchen"
        } else {
          return "Jahre Strom verbrauchen"
        }
      case EquivalenceTypes.fitness:
        if (value === 1) {
          return "Monat Fitnessstudio"
        } else {
          return "Monate Fitnessstudio"
        }
      case EquivalenceTypes.food:
        return "mal Essen bestellen"
      case EquivalenceTypes.heating:
        if (value === 1) {
          return "Jahr heizen"
        } else {
          return "Jahre heizen"
        }
      case EquivalenceTypes.message:
        if (value === 1) {
          return "gesendete Nachricht"
        } else {
          return "gesendete Nachrichten"
        }
      case EquivalenceTypes.musicStreaming:
        if (value === 1) {
          return "Monat Musikstreaming"
        } else {
          return "Monate Musikstreaming"
        }
      case EquivalenceTypes.phone:
        if (value === 1) {
          return "produziertes Smartphone"
        } else {
          return "produzierte Smartphones"
        }
      case EquivalenceTypes.showering:
        return "mal warm duschen"
      case EquivalenceTypes.streaming:
        if (value === 1) {
          return "Stunde Streaming"
        } else {
          return "Stunden Streaming"
        }
      case EquivalenceTypes.tvTower:
        return "mal auf den Fernsehturm"
      case EquivalenceTypes.washing:
        return "mal die Wäsche trocknen"
      case EquivalenceTypes.wilhelma:
        return "mal in die Wilhelma"
    }

    return ""
  }

  type: EquivalenceTypes
  icon: string
  message: string
  comparisonValue: number
  equalIcon: string
  mainType: EquivalenceMainType

  constructor(type: EquivalenceTypes, comparisonValue: number) {
    this.type = type
    this.icon = Equivalence.equivalenceIconMap.get(type) ?? ""
    this.message = Equivalence.getMessage(type, comparisonValue)
    this.comparisonValue = comparisonValue
    this.mainType = Equivalence.getMainTypeFromType(type)
    this.equalIcon = this.mainType === EquivalenceMainType.co2 ? icoEqualCO2 : icoEqualCost
  }
}
